import { ApiEndpoint } from '@/api/registry';
import { useApiQuery } from '@/api/useApiQuery';
import type { ScenarioListResponse } from '@/features/decisions/scenarios.api.types';
import { useTenant } from '@/providers/TenantProvider';

// Matches the service default so the first page here and the first page there are
// the same page. The service caps limit at 200.
export const SCENARIOS_PAGE_SIZE = 20;

// Matches the service's own status refresh window (STATUS_REFRESH_SECONDS = 10). Polling
// faster would just re-read the same cached value, since the service will not re-check
// Execution Service more often than that.
const RUNNING_POLL_MS = 10_000;

const TERMINAL_STATUSES: ReadonlySet<string> = new Set(['succeeded', 'failed']);

export interface UseScenariosParams {
  limit?: number;
  offset?: number;
  // Off for callers that only need the names, e.g. a scenario picker. Creating a scenario
  // already invalidates this query, so a picker stays current without polling for it.
  poll?: boolean;
}

// The backtest catalog: which scenarios exist and when each last ran.
//
// Archived scenarios are excluded, which is the service default — the endpoint takes
// include_archived, but nothing here surfaces archived rows yet, so passing it would
// only add a query param that never changes.
export function useScenarios({ limit = SCENARIOS_PAGE_SIZE, offset = 0, poll = true }: UseScenariosParams = {}) {
  const { tenantAlias } = useTenant();

  return useApiQuery<ScenarioListResponse>(
    tenantAlias,
    ApiEndpoint.ScenariosList,
    { query: { limit, offset } },
    {
      // A catalog read is cheap and the list is the page's whole content, so a
      // failure needs to surface as an error state rather than be retried behind a
      // spinner the user cannot interpret.
      retry: false,
      staleTime: 30_000,
      // Poll only while something can still change. A page of finished backtests stops
      // polling entirely, so an idle tab costs nothing; a page with one running backtest
      // updates without the user reloading. A scenario that has never run will not change
      // on its own either.
      //
      // `!= null` deliberately, not `!== null`: against a service that predates
      // latest_status the field is absent, and treating undefined as live would poll every
      // 10s forever on a list that can never update.
      refetchInterval: (query) => {
        if (!poll) return false;
        const rows = query.state.data?.scenarios ?? [];
        const anyLive = rows.some((r) => r.latest_status != null && !TERMINAL_STATUSES.has(r.latest_status));
        return anyLive ? RUNNING_POLL_MS : false;
      },
    },
  );
}
